<template>
	<div class="kytaristka-song song-content"
		 :style="{ fontSize: detailFontSize + 'em' }"
		 :class="{
			'hide-chords': detailHideChords,
			'two-column': isTwoColumnLayout,
			'night-mode': nightMode,
			'without-headers': !songJson.containsHeader
		}">
		<div v-for="block in songJson.blocks" class="block" :class="`block-${block.type}`" :key="Math.random()" :data-random="Math.random()">
			<template v-if="'content' in block">
				<div class="block-header" :class="{ 'block-header-with-chords': isBlockHeaderWithChords(block) }">{{ blockHeader(block) }}</div>
				<div class="block-content">
					<div class="line"
						 :class="{ 'line-with-chords': line.chords.length > 0, 'line-only-chords': line.lyrics.replace(/^\s+|\s+$/g, '').length === 0 }"
						 v-for="line in block.content"
						 :key="Math.random()"
						 v-html="lineHtml(line)" />
				</div>
			</template>
			<template v-if="block.type === 'reference'">
				<div :class="`block-reference-${block.reference}`" v-if="block.value">{{ block.value }}</div>
				<div :class="`block-reference-${block.reference}`" v-else-if="block.reference === 'chorus'">Refrén</div>
				<div :class="`block-reference-${block.reference}`" v-else-if="block.reference === 'bridge'">Bridge</div>
			</template>
			<template v-if="block.type === 'comment'">
				<div :class="block.marginBottom ? null : 'block-comment-no-margin-bottom'">{{ block.value }}</div>
			</template>
		</div>
	</div>
</template>

<script>
	import Vue from "vue";
	import {mapState} from "vuex";

	export default {
		name: "SongContentKytaristka",
		props: {
			song: Object
		},
		mounted() {
			document.addEventListener("ProcessChords", this.processChords);
			Vue.nextTick(() => {
				this.processChords();
			})
		},
		destroy() {
			document.removeEventListener("ProcessChords", this.processChords);
		},
		watch: {
			songJson() {
				Vue.nextTick(() => {
					this.processChords();
				})
			}
		},
		recomputed: {
			isTwoColumnLayout() {
				const value = localStorage.getItem("songs:" + this.song.id + ":twoColumns");
				return value !== null;
			}
		},
		computed: {
			...mapState([ "detailFontSize", "detailHideChords", "nightMode" ]),
			songJson() {
				return Vue.$chordproJson(this.song.content, this.song.transpose)
			}
		},
		methods: {
			layoutChanged() {
				this.$recompute("isTwoColumnLayout");
				Vue.nextTick(() => {
					this.processChords();
				})
			},
			lineHtml({ lyrics, chords }) {
				for (const chord of [...chords].reverse()) {
					const formattedChord = chord.chord.replace(/#/g, "♯")
					lyrics = [
						lyrics.slice(0, chord.position),
						`<span class="chord" data-offset="${chord.offset}" data-position="${chord.position}"><span>${formattedChord}</span></span>`,
						lyrics.slice(chord.position)
					].join('');
				}

				return lyrics;
			},
			blockHeader(block) {
				if (block.value) return `${block.value}.`;
				if (block.type === 'chorus') return "Ref.";
			},
			isBlockHeaderWithChords(block) {
				if (!("content" in block)) return false;
				if (!block.content || block.content.length === 0) return false;
				return block.content[0].chords.length > 0;
			},
			processChords() {
				Array.from(this.$el.getElementsByClassName("chord")).map(chord => {
					let nextChord = chord.nextSibling;
					if (nextChord !== null) {
						if (nextChord.className !== "chord") {
							nextChord = nextChord.nextSibling;
						}
						if (nextChord !== null && nextChord.className === "chord") {
							nextChord.style.marginLeft = 0;
							if (chord.offsetTop === nextChord.offsetTop) {
								const collisionWidth = (chord.offsetLeft + chord.children[0].getBoundingClientRect().width) - nextChord.offsetLeft;
								if (collisionWidth > 0) {
									nextChord.style.marginLeft = collisionWidth + 2 + "px";
								}
							}
						}
					}
				});

				this.$nextTick(() => {
					Array.from(this.$el.getElementsByClassName("line-with-chords")).map(line => {
						const prevLine = line.previousSibling;
						if (prevLine && !prevLine.classList.contains("line-with-chords")) {
							prevLine.style.paddingBottom = "0.65em"
						}
					});
				})

				this.$nextTick(() => {
					Array.from(this.$el.getElementsByClassName("line")).map(line2 => {
						if (!line2.classList.contains("line-with-chords")) {
							const prevLine = line2.previousSibling;
							if (prevLine && prevLine.classList.contains("line-with-chords")) {
								line2.style.marginTop = "-0.6em"
							}
						}
					});
				});
			}
		}
	}
</script>

<style lang="scss">
	div.kytaristka-song {
		font-family: "Inconsolata", "Courier", monospace;
		line-height: 1.1em;
		column-rule: rgba(0, 0, 0, 0.05) 1px solid;

		&.hide-chords {
			span.chord {
				display: none !important;
			}

			div.line {
				padding-bottom: 0 !important;
			}

			div.line-with-chords {
				line-height: 1.3em !important;
				margin-top: 0 !important;
			}

			div.block-header-with-chords {
				padding-top: 4px !important;
			}
		}

		&.night-mode {
			span.chord {
				span {
					background-color: rgba(0, 0, 0, 0.5) !important;
					color: rgba(255, 255, 255, 0.8) !important;
				}
			}

			div.block-comment, div.block-reference {
				div {
					background-color: rgba(0, 0, 0, 0.5) !important;
					color: rgba(255, 255, 255, 0.8) !important;
				}
			}

			&.two-column {
				column-rule: 1px solid rgba(255, 255, 255, 0.15);
			}
		}

		&.two-column {
			column-count: 2;
			column-gap: 30px;
			column-fill: balance;
			column-rule: 1px solid rgba(0, 0, 0, 0.15);
		}

		&.without-headers {
			margin-left: -25px;
		}

		div.block {
			margin-left: 5px;
			padding: 0.8em 0;
			display: flex;
			break-inside: avoid-column;

			&.block-verse {
			}

			&.block-chorus {
				border-left: 2px solid rgba(232, 90, 79, 0.5);
			}

			&.block-bridge {
				border-left: 2px solid rgba(79, 82, 232, 0.2);
			}

			&.block-reference {
				div {
					margin-left: 17px;
					background-color: rgba(255, 255, 255, 0.5);
					padding: 2px 4px;
					border-radius: 4px;
				}

				&.block-reference-chorus {
					font-weight: 600;
				}

				&.block-reference-bridge {
					font-weight: 600;
				}
			}

			&.block-comment {
				padding: 0.4em 0;
				div {
					margin-left: 17px;
					font-style: italic;
					opacity: 0.8;
					background-color: rgba(255, 255, 255, 0.5);
					padding: 2px 4px;
					border-radius: 4px;

					&.block-comment-no-margin-bottom {
						margin-bottom: -20px;
					}
				}
			}

			div.block-header {
				width: 2.5em;
				line-height: 1.4rem;
				font-size: 0.95rem;
				text-align: right;
				padding-right: 5px;
				font-weight: 800;
				padding-top: 1px;

				&.block-header-with-chords {
					padding-top: 12px;
				}
			}

			div.block-content {
				div.line {
					line-height: 1.3em;

					span.chord {
						word-wrap: normal;
						width: 0;
						font-weight: 800;
						display: inline-block;
						top: -1.15em;
						position: relative;
						margin-left: 0;

						span {
							margin-left: -4px;
							border-radius: 4px;
							background-color: rgba(255, 255, 255, 0.5);
							padding: 2px 4px;
						}
					}

					&.line-with-chords {
						line-height: 2.55em;
					}

					&.line-only-chords {
						padding-top: 0;

						span.chord {
							top: 0;
						}
					}
				}
			}
		}
	}
</style>
